import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@parleyride/supabase';

export interface GeocodingFeature {
  text: string;
  place_name: string;
  center: [number, number];
  properties?: { category?: string; place_id?: string };
}

export interface RouteGeometry {
  type: 'LineString';
  coordinates: [number, number][];
}

export interface RouteData {
  geometry: RouteGeometry;
  distance: number;
  duration: number;
  trafficDuration?: number;
  congestion?: 'low' | 'moderate' | 'heavy' | string;
  polyline?: string;
}

export interface NearbyDriver {
  driver_id: string;
  first_name: string;
  last_name: string;
  avatar_url: string | null;
  rating: number | null;
  completed_rides: number | null;
  vehicle_make: string | null;
  vehicle_model: string | null;
  vehicle_color: string | null;
  vehicle_plate: string | null;
  vehicle_type: string | null;
  /**
   * Distance from the searched point, bucketed. Since `20260829000002` the
   * search centre and the driver's point are both snapped to a 250 m grid and
   * the distance is rounded UP to the next 250 m, so this is always a
   * non-negative multiple of 250 and is an upper bound on the true distance,
   * not the true distance. Two searches from anywhere inside one grid cell
   * return the same value — that is what stops repeated calls trilaterating
   * the driver. Present it as "within N m".
   */
  distance_meters: number;
}

export async function geocode(
  supabase: SupabaseClient<Database>,
  query: string,
): Promise<GeocodingFeature[]> {
  if (!query.trim()) return [];
  const { data, error } = await supabase.functions.invoke('location', {
    body: { action: 'geocode', params: { query } },
  });
  if (error) throw new Error(error.message || 'Geocoding failed');
  return data?.features ?? [];
}

export async function reverseGeocode(
  supabase: SupabaseClient<Database>,
  coordinates: [number, number],
): Promise<string> {
  const { data, error } = await supabase.functions.invoke('location', {
    body: { action: 'reverseGeocode', params: { coordinates } },
  });
  if (error) throw new Error(error.message || 'Reverse geocoding failed');
  const feature = data?.features?.[0];
  return feature?.place_name ?? 'Unknown location';
}

export async function getRoute(
  supabase: SupabaseClient<Database>,
  from: [number, number],
  to: [number, number],
  includeTraffic = true,
): Promise<RouteData | null> {
  const { data, error } = await supabase.functions.invoke('location', {
    body: { action: 'getRoute', params: { from, to, includeTraffic } },
  });
  if (error) throw new Error(error.message || 'Failed to fetch route');
  const route = data?.routes?.[0];
  if (!route) return null;
  return {
    geometry: route.geometry,
    distance: route.distance,
    duration: route.duration,
    trafficDuration: route.duration_in_traffic ?? route.duration,
    congestion: route.congestion_level ?? 'low',
    polyline: route.polyline ?? route.geometry_polyline,
  };
}

export async function getMapProviderKey(
  supabase: SupabaseClient<Database>,
  provider: 'mapbox' | 'google' = 'mapbox',
): Promise<string | null> {
  const action = provider === 'google' ? 'getGoogleMapsApiKey' : 'getMapboxToken';
  const { data, error } = await supabase.functions.invoke('location', {
    body: { action },
  });
  if (error) throw new Error(error.message || `Failed to fetch ${provider} key`);
  return data?.mapboxToken ?? data?.apiKey ?? data?.key ?? null;
}

export async function findNearbyDrivers(
  supabase: SupabaseClient<Database>,
  lat: number,
  lng: number,
  radiusMeters = 5000,
): Promise<NearbyDriver[]> {
  const { data, error } = await supabase.rpc('find_nearby_drivers', {
    lat,
    lng,
    radius_meters: radiusMeters,
  });
  if (error) throw new Error(error.message || 'Failed to find nearby drivers');
  return (data ?? []) as NearbyDriver[];
}
